import { ActiveEventProvider } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { CalendarColorsProvider } from "@clockwise/web-commons/src/util/CalendarColorsContext";
import { CalendarProvider, WeekStartDays } from "@clockwise/web-commons/src/util/CalendarContext";
import { useExperiment } from "@clockwise/web-commons/src/util/ExperimentsContext";
import { CalendarAction } from "@clockwise/web-commons/src/util/calendarReducer";
import { getPreviousWeekday } from "@clockwise/web-commons/src/util/getPreviousWeekday";
import { getSequentialDays } from "@clockwise/web-commons/src/util/getSequentialDays";
import { DateTime } from "luxon";
import * as React from "react";
import { useLocalStorage } from "usehooks-ts";
import { useFeatureFlag } from "../../launch-darkly";
import { RouteRenderer } from "../route-renderer";
import { PlannerProvider } from "./Context";
import { PlannerAIBanner } from "./PlannerAIBanner";
import { CalendarWeek } from "./calendar-week/CalendarWeek";
import { CalendarColorsSync } from "./hooks/CalendarColorsSync";
import { usePlannerEventCards } from "./hooks/usePlannerEventCards";
import usePlannerMetaData from "./hooks/usePlannerMetaData";
import { StorageKeyPrefixProvider } from "./hooks/useStorageKeyPrefix";
import { PlannerStatePersistance } from "./planner-state-persistance/PlannerStatePersistance";
import { PlannerToolbar } from "./planner-toolbar/PlannerToolbar";
import { PlannerSidebar } from "./sidebar";

// setup inital state for calendar
const dateTime = DateTime.now().startOf("day");
const startOfWeek = getPreviousWeekday(dateTime, "sunday");
const visibleDates = getSequentialDays(startOfWeek, 7).map((date) => date.toISODate());

const initCalendarState = {
  focusedDate: dateTime.toISODate(),
  visibleDates,
  weekendsShown: true,
  weekStartDay: "sunday" as WeekStartDays,
};

export const Planner = () => {
  const {
    userCalendarIds,
    colorSettings,
    workingHourBounds,
    workingHours,
    loading,
    error,
  } = usePlannerMetaData();

  const [showWeekends, setShowWeekends] = useLocalStorage<boolean>("planner:showWeekends", true);
  const [hasClockwiseAI] = useFeatureFlag("ClockwiseAI");
  // update intial state if preferences are persisted to local storage
  initCalendarState.weekendsShown = showWeekends;
  initCalendarState.weekStartDay = showWeekends ? "sunday" : "monday";

  const isMultiCalendarSelectExperimentOn = useExperiment("MultiCalendarSelect");

  const handleChange = (action: CalendarAction) => {
    if (action.type === "set-weekendsShown") {
      setShowWeekends(action.payload);
    }
  };

  return (
    <RouteRenderer loading={loading} error={!!error}>
      <CalendarColorsProvider initValue={{}}>
        <CalendarProvider initValue={initCalendarState} onChange={handleChange}>
          <CalendarColorsSync />
          <ActiveEventProvider>
            <PlannerStatePersistance />
            <StorageKeyPrefixProvider prefix="planner">
              <PlannerProvider
                colorSettings={colorSettings}
                useEventCardsHook={usePlannerEventCards}
                userCalendarIds={userCalendarIds}
                workingHourBounds={workingHourBounds}
                workingHours={workingHours}
              >
                <div className="cw-flex cw-w-full cw-transition-all">
                  <div className="cw-w-full cw-h-screen cw-overflow-hidden cw-mr-[1px]">
                    {hasClockwiseAI && <PlannerAIBanner />}
                    <PlannerToolbar showCalendarPeek={isMultiCalendarSelectExperimentOn} />
                    <CalendarWeek />
                  </div>
                  <PlannerSidebar />
                </div>
              </PlannerProvider>
            </StorageKeyPrefixProvider>
          </ActiveEventProvider>
        </CalendarProvider>
      </CalendarColorsProvider>
    </RouteRenderer>
  );
};
